import React, { useEffect } from "react";
import styled from "styled-components";
import GoogleFormEmbed from "../components/GoogleFormEmbed";
import { GOOGLE_FORM_URL } from "../env";
import { trackPageView, trackFormSubmission } from "../utils/tracking";

const PageHeader = styled.div`
  background: linear-gradient(135deg, #ff9a9e 0%, #fad0c4 100%);
  padding: 3rem 0;
  color: white;
  text-align: center;
`;

const HeaderContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 1rem;
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  opacity: 0.9;
`;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 3rem 20px;
`;

const Section = styled.section`
  margin-bottom: 3rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  margin-bottom: 1.5rem;
  color: #333;
`;

const Text = styled.p`
  margin-bottom: 1.5rem;
  line-height: 1.7;
  color: #555;
`;

const FormContainer = styled.div`
  margin-top: 2rem;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 1rem;
  background-color: #fff;
`;

interface SubmissionPageProps {
  // Add any props if needed
}

const SubmissionPage: React.FC<SubmissionPageProps> = () => {
  // Get the Google Form URL from our environment variables
  const formUrl = GOOGLE_FORM_URL;

  useEffect(() => {
    // Track page view when submission page loads
    trackPageView("submission");
  }, []);

  // Function to handle form submission tracking
  // Since we can't directly track Google Form submissions, we'll track when the form is loaded
  useEffect(() => {
    // Track that the form was viewed
    trackFormSubmission("gift_idea_submission_form_viewed", {
      form_url: formUrl,
      timestamp: new Date().toISOString(),
    });

    // Add event listener to track when user interacts with the iframe
    const handleFormInteraction = () => {
      trackFormSubmission("gift_idea_submission_form_interaction", {
        form_url: formUrl,
        timestamp: new Date().toISOString(),
      });

      // Remove the event listener after first interaction
      document.removeEventListener("click", handleFormInteraction);
    };

    // Add event listener to the document
    document.addEventListener("click", handleFormInteraction);

    // Clean up event listener on component unmount
    return () => {
      document.removeEventListener("click", handleFormInteraction);
    };
  }, [formUrl]);

  return (
    <>
      <PageHeader>
        <HeaderContent>
          <Title>Share Your Gift Ideas</Title>
          <Subtitle>
            Help others find the perfect gift by sharing your recommendations
          </Subtitle>
        </HeaderContent>
      </PageHeader>

      <Container>
        <Section>
          <SectionTitle>Submit Your Gift Ideas</SectionTitle>
          <Text>
            Do you have a great gift idea that others might love? Share it with
            our community! Your suggestions will help others find the perfect
            gift for their loved ones.
          </Text>
          <Text>
            Fill out the form below with details about your gift idea. Be sure
            to include:
          </Text>
          <ul style={{ marginBottom: "1.5rem", paddingLeft: "1.5rem" }}>
            <li>Gift name and description</li>
            <li>Suitable occasions (birthdays, anniversaries, etc.)</li>
            <li>Ideal recipients (who would love this gift?)</li>
            <li>Price range</li>
            <li>Where to purchase (optional)</li>
            <li>Image URL (optional)</li>
          </ul>
          <FormContainer>
            <GoogleFormEmbed
              formUrl={formUrl}
              height="1200px"
              title="Submit Your Gift Idea"
              description="Have a great gift idea? Share it with our community by filling out this form."
            />
          </FormContainer>
        </Section>
      </Container>
    </>
  );
};

export default SubmissionPage;
